export const TIME_FORMATS = [
  {value: 'HH:MM:SS', label: 'HH:MM:SS'},
  {value: 'MM:SS', label: 'MM:SS'},
  {value: 'MM:SS.hh', label: 'MM:SS.hh'},
  {value: 'SS.hh', label: 'SS.hh'},
];

export const isBlankQuestionAnswer = value => {
  if (Array.isArray(value)) return value.length === 0;
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return false;
  if (typeof value === 'boolean') return false;

  if (typeof value === 'object') {
    // Time objects: require a format AND at least one time part.
    if (
      Object.prototype.hasOwnProperty.call(value, 'format') &&
      (Object.prototype.hasOwnProperty.call(value, 'HH') ||
        Object.prototype.hasOwnProperty.call(value, 'MM') ||
        Object.prototype.hasOwnProperty.call(value, 'SS') ||
        Object.prototype.hasOwnProperty.call(value, 'hh'))
    ) {
      const format = typeof value.format === 'string' ? value.format.trim() : '';
      const parts = [value.HH, value.MM, value.SS, value.hh];
      const allPartsBlank = parts.every(
        part => part === null || part === undefined || (typeof part === 'string' && part.trim() === '')
      );
      return format === '' || allPartsBlank;
    }

    const values = Object.values(value);
    if (values.length === 0) return true;
    return values.every(isBlankQuestionAnswer);
  }

  return false;
};

export const normalizeHeightValue = value => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return {
      feet: value.feet ?? '',
      inch: value.inch ?? value.inches ?? '',
    };
  }

  if (typeof value !== 'string') return {feet: '', inch: ''};

  const raw = value.trim();
  if (!raw) return {feet: '', inch: ''};

  const match = raw.match(/^\s*(\d+)\s*'\s*(\d+)?\s*"?\s*$/);
  if (match) return {feet: match[1] ?? '', inch: match[2] ?? ''};

  const onlyFeet = raw.match(/^\s*(\d+)\s*$/);
  if (onlyFeet) return {feet: onlyFeet[1] ?? '', inch: ''};

  return {feet: '', inch: ''};
};

export const normalizeAddressValue = value => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return {
      street: value.street ?? '',
      city: value.city ?? '',
      state: value.state ?? '',
      zip: value.zip ?? '',
    };
  }

  if (typeof value === 'string' && value.trim()) {
    return {street: value.trim(), city: '', state: '', zip: ''};
  }

  return {street: '', city: '', state: '', zip: ''};
};

const normalizeTimeParts = parts => ({
  HH: parts?.HH ?? '',
  MM: parts?.MM ?? '',
  SS: parts?.SS ?? '',
  hh: parts?.hh ?? '',
});

export const normalizeTimeValue = value => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return {
      format: value.format ?? '',
      ...normalizeTimeParts(value),
    };
  }

  if (typeof value !== 'string') return {format: '', HH: '', MM: '', SS: '', hh: ''};

  const raw = value.trim();
  if (!raw) return {format: '', HH: '', MM: '', SS: '', hh: ''};

  // Legacy datetime-local (YYYY-MM-DDTHH:MM)
  const dt = raw.match(/^\d{4}-\d{2}-\d{2}T(\d{2}):(\d{2})/);
  if (dt) {
    return {format: 'HH:MM:SS', HH: dt[1] ?? '', MM: dt[2] ?? '', SS: '0', hh: ''};
  }

  // HH:MM:SS(.hh)
  const hhmmss = raw.match(/^(\d+):(\d+):(\d+)(?:\.(\d+))?$/);
  if (hhmmss) {
    return {
      format: hhmmss[4] ? 'HH:MM:SS' : 'HH:MM:SS',
      HH: hhmmss[1] ?? '',
      MM: hhmmss[2] ?? '',
      SS: hhmmss[3] ?? '',
      hh: hhmmss[4] ?? '',
    };
  }

  // MM:SS(.hh)
  const mmss = raw.match(/^(\d+):(\d+)(?:\.(\d+))?$/);
  if (mmss) {
    return {
      format: mmss[3] ? 'MM:SS.hh' : 'MM:SS',
      HH: '',
      MM: mmss[1] ?? '',
      SS: mmss[2] ?? '',
      hh: mmss[3] ?? '',
    };
  }

  // SS(.hh)
  const ss = raw.match(/^(\d+)(?:\.(\d+))?$/);
  if (ss) {
    return {
      format: ss[2] ? 'SS.hh' : 'MM:SS',
      HH: '',
      MM: '',
      SS: ss[1] ?? '',
      hh: ss[2] ?? '',
    };
  }

  return {format: '', HH: '', MM: '', SS: '', hh: ''};
};

export const normalizeExerciseValue = value => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return {
      sets: value.sets ?? '',
      reps: value.reps ?? '',
      weight: value.weight ?? '',
      notes: value.notes ?? '',
    };
  }

  if (typeof value === 'string' && value.trim()) {
    return {sets: '', reps: '', weight: '', notes: value.trim()};
  }

  return {sets: '', reps: '', weight: '', notes: ''};
};
